import React, { useEffect, useMemo, useState } from 'react';
import {
  FiZap, FiPlus, FiTrash2, FiLoader, FiRefreshCw, FiEdit3,
  FiCheck, FiX, FiSend, FiEye, FiAlertCircle, FiCheckCircle,
  FiTrendingUp, FiTarget, FiCalendar, FiClock, FiList, FiImage,
} from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';
import QueueCalendarView from './QueueCalendarView';

type TopicSuggestion = {
  id: string;
  topic: string;
  suggested_angle: string;
  suggested_category: string;
  virality_signal: number;
  example_tweets: string[];
  underlying_tweet_ids: string[];
  generated_at: string;
  status: string;
};

/**
 * Content Queue Manager — the pro-tier scheduler feature.
 *
 *   1) List all pending / approved / posted queue rows
 *   2) Add manual tweet (text + category)
 *   3) Bulk-generate N tweets via Fable 5 (POST /api/growth-api?action=bulk-generate)
 *   4) Delete pending rows
 *   5) Trigger "post-from-queue now" (admin override)
 *   6) Show category balance stats
 *
 * Cron /api/growth-cron?action=post-from-queue runs hourly and picks
 * one row when: (a) daily cap not hit, (b) min gap OK, (c) current hour
 * is in top-N peak hours per twitter_hourly_engagement heatmap.
 */

type QueueRow = {
  id: string;
  text: string;
  content_category: string;
  source: string;
  status: 'pending' | 'approved' | 'posting' | 'posted' | 'failed' | 'canceled';
  scheduled_at: string | null;
  posted_at: string | null;
  posted_tweet_id: string | null;
  media_url: string | null;
  priority: number;
  error_message: string | null;
  ai_cost_usd: number | null;
  created_at: string;
};

type QueueStats = {
  by_category: { content_category: string; pending_count: number; posted_count: number; failed_count: number; last_posted_at: string | null }[];
  total_pending: number;
  total_posted: number;
  total_failed: number;
  projected_schedule?: {
    next_slot_at: string | null;
    queue_empty_at: string | null;
    peak_hours_utc: number[];
    config: {
      max_per_day: number;
      min_gap_min: number;
      peak_top_n: number;
      bootstrap: boolean;
    };
    upcoming_slots: string[];
  };
};

// Human-friendly "in Xh Ym" or "in Xd Yh" from an ISO time
function humanUntil(iso: string | null): string {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'now';
  const min  = Math.floor(ms / 60_000);
  const hr   = Math.floor(min / 60);
  const days = Math.floor(hr / 24);
  if (days >= 1) return `in ${days}d ${hr % 24}h`;
  if (hr >= 1)   return `in ${hr}h ${min % 60}m`;
  return `in ${min}m`;
}

function formatSlotShort(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const isToday    = d.toDateString() === now.toDateString();
  const isTomorrow = d.toDateString() === new Date(now.getTime() + 86400_000).toDateString();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (isToday)    return `Today ${hh}:${mm}`;
  if (isTomorrow) return `Tomorrow ${hh}:${mm}`;
  const day = d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  return `${day} ${hh}:${mm}`;
}

const CATEGORIES = [
  'general', 'verdict', 'discipline', 'macro',
  'lesson', 'question', 'hook', 'thread_teaser', 'meme',
];

const ANGLES = [
  { value: 'discipline',   label: 'Discipline / Risk' },
  { value: 'macro-recap',  label: 'Macro observation' },
  { value: 'lessons',      label: 'Lessons from mistakes' },
  { value: 'myths',        label: 'Myths debunked' },
  { value: 'strategy',     label: 'Setup / process' },
  { value: 'question',     label: 'Provocative question' },
  { value: 'hook',         label: 'Contrarian hook' },
  { value: 'contrarian',   label: 'Contrarian take' },
  { value: 'story',        label: 'Micro-story' },
];

const CATEGORY_COLOR: Record<string, string> = {
  general:       'bg-white/10 text-gray-300 border-white/20',
  verdict:       'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
  discipline:    'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  macro:         'bg-amber-500/20 text-amber-300 border-amber-500/40',
  lesson:        'bg-purple-500/20 text-purple-300 border-purple-500/40',
  question:      'bg-sky-500/20 text-sky-300 border-sky-500/40',
  hook:          'bg-rose-500/20 text-rose-300 border-rose-500/40',
  thread_teaser: 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/40',
  meme:          'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
};

const STATUS_COLOR: Record<string, string> = {
  pending:  'bg-white/10 text-gray-300 border-white/20',
  approved: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  posting:  'bg-amber-500/20 text-amber-300 border-amber-500/40 animate-pulse',
  posted:   'bg-cyan-500/20 text-cyan-300 border-cyan-500/40',
  failed:   'bg-rose-500/20 text-rose-300 border-rose-500/40',
  canceled: 'bg-slate-700/40 text-slate-400 border-slate-600/40',
};

const ContentQueueManager: React.FC<{ stats?: QueueStats; onRefresh?: () => void }> = ({ stats, onRefresh }) => {
  const [rows, setRows] = useState<QueueRow[]>([]);
  const [pendingRows, setPendingRows] = useState<QueueRow[]>([]);  // priority-sorted, for calendar pairing
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'posted' | 'failed'>('pending');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('calendar');

  // Manual add
  const [manualText, setManualText] = useState('');
  const [manualCategory, setManualCategory] = useState('general');
  const [adding, setAdding] = useState(false);

  // Bulk generate
  const [bulkTopic, setBulkTopic] = useState('');
  const [bulkAngle, setBulkAngle] = useState('discipline');
  const [bulkCount, setBulkCount] = useState(10);
  const [bulkCategory, setBulkCategory] = useState('discipline');
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [lastBatchInfo, setLastBatchInfo] = useState<{ count: number; cost: number } | null>(null);

  // Post-now
  const [posting, setPosting] = useState(false);
  const [postResult, setPostResult] = useState<any>(null);

  // Trending topics (Fable 5 clusters viral pool)
  const [topics, setTopics] = useState<TopicSuggestion[]>([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsRefreshing, setTopicsRefreshing] = useState(false);
  const [topicsInfo, setTopicsInfo] = useState<string>('');
  const [topicsError, setTopicsError] = useState<string | null>(null);
  const [topicsGeneratedAt, setTopicsGeneratedAt] = useState<string | null>(null);

  const loadTopics = async () => {
    setTopicsLoading(true);
    setTopicsError(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      const res = await fetch('/api/growth-api?action=suggest-topics', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      setTopics(json.topics || []);
      setTopicsGeneratedAt(json.generated_at || null);
      if (json.hint) setTopicsInfo(json.hint);
    } catch (e: any) {
      setTopicsError(e?.message || String(e));
    } finally {
      setTopicsLoading(false);
    }
  };

  const refreshTopics = async () => {
    setTopicsRefreshing(true);
    setTopicsError(null);
    setTopicsInfo('');
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      const res = await fetch('/api/growth-api?action=suggest-topics&refresh=1', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      if (json.skipped) {
        setTopicsInfo(json.reason || 'skipped');
      } else {
        setTopics(json.topics || []);
        setTopicsGeneratedAt(json.generated_at || new Date().toISOString());
        setTopicsInfo(`Fresh batch: ${json.topic_count} topics, pool size ${json.pool_size}, cost $${(json.cost_usd || 0).toFixed(4)}`);
      }
    } catch (e: any) {
      setTopicsError(e?.message || String(e));
    } finally {
      setTopicsRefreshing(false);
    }
  };

  useEffect(() => { loadTopics(); }, []);

  const useTopic = (topic: TopicSuggestion) => {
    setBulkTopic(topic.topic);
    if (topic.suggested_angle) setBulkAngle(topic.suggested_angle);
    if (topic.suggested_category) setBulkCategory(topic.suggested_category);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  const load = async () => {
    setLoading(true);
    let query = supabase
      .from('tweet_queue')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);
    if (filter === 'pending') query = query.in('status', ['pending', 'approved']);
    else if (filter === 'posted') query = query.eq('status', 'posted');
    else if (filter === 'failed') query = query.eq('status', 'failed');
    if (categoryFilter) query = query.eq('content_category', categoryFilter);
    const { data } = await query;
    setRows((data || []) as any);

    // Pending rows sorted priority-first for calendar pairing (matches
    // queue-poster picking order).
    const { data: pending } = await supabase
      .from('tweet_queue')
      .select('*')
      .in('status', ['pending', 'approved'])
      .order('scheduled_at', { ascending: true, nullsFirst: false })
      .order('priority',     { ascending: false })
      .order('created_at',   { ascending: true })
      .limit(100);
    setPendingRows((pending || []) as any);

    setLoading(false);
  };

  useEffect(() => { load(); }, [filter, categoryFilter]);

  const clickCategory = (cat: string) => {
    setCategoryFilter(prev => prev === cat ? null : cat);  // toggle
    setFilter('pending');
    // Scroll to Queue rows section
    setTimeout(() => {
      document.getElementById('queue-rows-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const addManual = async () => {
    if (!manualText.trim()) return;
    setAdding(true);
    const { data: userData } = await supabase.auth.getUser();
    await supabase.from('tweet_queue').insert({
      text: manualText.trim(),
      content_category: manualCategory,
      source: 'manual',
      status: 'pending',
      created_by: userData?.user?.id,
    });
    setManualText('');
    setAdding(false);
    await load();
    onRefresh?.();
  };

  const bulkGenerate = async () => {
    if (!bulkTopic.trim()) return;
    setBulkGenerating(true);
    setBulkError(null);
    setLastBatchInfo(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      const res = await fetch('/api/growth-api?action=bulk-generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          topic: bulkTopic.trim(),
          angle: bulkAngle,
          count: Number(bulkCount),
          category: bulkCategory,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      setBulkTopic('');
      setLastBatchInfo({ count: json.produced_count, cost: json.cost_usd });
      await load();
      // Await parent so stats.projected_schedule refetches BEFORE user
      // scrolls to the calendar. Prevents "queue count updated but
      // calendar still shows old slots" perception.
      await onRefresh?.();
    } catch (e: any) {
      setBulkError(e?.message || String(e));
    } finally {
      setBulkGenerating(false);
    }
  };

  const deleteRow = async (id: string) => {
    if (!confirm('Delete this queue row permanently?')) return;
    await supabase.from('tweet_queue').delete().eq('id', id);
    await load();
    onRefresh?.();
  };

  const setPriority = async (id: string, priority: number) => {
    await supabase.from('tweet_queue').update({ priority }).eq('id', id);
    await load();
  };

  const editText = async (row: QueueRow, newText: string) => {
    if (newText === row.text) return;
    await supabase.from('tweet_queue').update({ text: newText }).eq('id', row.id);
    await load();
  };

  const postNextNow = async () => {
    setPosting(true);
    setPostResult(null);
    try {
      const res = await fetch('/api/growth-cron?action=post-from-queue&force=1');
      const json = await res.json();
      setPostResult(json);
      await load();
      // Await parent so stats.projected_schedule refetches BEFORE user
      // scrolls to the calendar. Prevents "queue count updated but
      // calendar still shows old slots" perception.
      await onRefresh?.();
    } catch (e: any) {
      setPostResult({ error: e?.message || String(e) });
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Trending topics — Fable 5 clusters what's viral on trader Twitter */}
      <div className="rounded-2xl border border-rose-500/20 bg-rose-500/[0.03] p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FiTrendingUp className="w-4 h-4 text-rose-400" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-rose-400">
              🔥 Trending trader topics — click to auto-fill
            </span>
          </div>
          <div className="flex items-center gap-2">
            {topicsGeneratedAt && (
              <span className="text-[10px] font-mono text-gray-500">
                {new Date(topicsGeneratedAt).toLocaleString()}
              </span>
            )}
            <button
              onClick={refreshTopics}
              disabled={topicsRefreshing}
              className="px-2.5 py-1 rounded-md bg-rose-500/20 border border-rose-500/40 text-rose-100 text-[10px] font-mono hover:bg-rose-500/30 disabled:opacity-40 flex items-center gap-1"
              title="Force new batch (spends ~$0.10 Fable 5 tokens)"
            >
              {topicsRefreshing ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiRefreshCw className="w-3 h-3" />}
              {topicsRefreshing ? 'Clustering…' : 'Refresh'}
            </button>
          </div>
        </div>

        {topicsLoading && !topics.length ? (
          <div className="text-xs text-gray-500 font-mono py-6 text-center">Loading topics…</div>
        ) : topics.length === 0 ? (
          <div className="text-xs text-gray-500 font-mono py-4">
            {topicsInfo || 'No topics yet. Click Refresh to generate the first batch (needs 15+ tweets in the trending pool — run viral finder + competitor scanner first).'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {topics.map(t => (
              <button
                key={t.id}
                onClick={() => useTopic(t)}
                className="text-left rounded-lg border border-white/10 bg-black/30 hover:border-rose-500/40 hover:bg-rose-500/5 p-3 transition-colors group"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="text-sm font-semibold text-white group-hover:text-rose-200 leading-snug">
                    {t.topic}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="text-lg font-black text-rose-400 tabular-nums">{Math.round(Number(t.virality_signal || 0))}</div>
                    <div className="text-[9px] font-mono text-gray-500 uppercase">score</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
                    {t.suggested_angle}
                  </span>
                  <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                    {t.suggested_category}
                  </span>
                  <span className="text-[9px] font-mono text-gray-500">
                    from {(t.underlying_tweet_ids || []).length} tweets
                  </span>
                </div>
                {(t.example_tweets || []).length > 0 && (
                  <div className="text-[10px] text-slate-400 italic line-clamp-2">
                    "{t.example_tweets[0]}"
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {topicsInfo && (
          <div className="mt-3 text-[10px] font-mono text-emerald-400 flex items-center gap-1">
            <FiCheckCircle className="w-3 h-3" /> {topicsInfo}
          </div>
        )}
        {topicsError && (
          <div className="mt-3 text-[10px] font-mono text-rose-400 flex items-center gap-1">
            <FiAlertCircle className="w-3 h-3" /> {topicsError}
          </div>
        )}
        <div className="mt-3 text-[10px] font-mono text-gray-500 leading-relaxed">
          Fable 5 clusters viral candidates + tracked competitor tweets from the last 72h into unique themes.
          Click a topic → the bulk-generate form below auto-fills.
        </div>
      </div>

      {/* Schedule Calendar — projected posting slots */}
      {stats?.projected_schedule && (
        <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.03] p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <FiCalendar className="w-4 h-4 text-cyan-400" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400">
                Auto-post schedule
              </span>
              {stats.projected_schedule.config.bootstrap && (
                <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40" title="No engagement heatmap data yet — every hour treated as peak">
                  bootstrap mode
                </span>
              )}
            </div>
            <div className="text-[10px] font-mono text-gray-500">
              Cap: {stats.projected_schedule.config.max_per_day}/day · gap: {stats.projected_schedule.config.min_gap_min}min · top {stats.projected_schedule.config.peak_top_n} peak {stats.projected_schedule.config.peak_top_n === 1 ? 'hour' : 'hours'}
            </div>
          </div>

          {/* Key numbers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <FiClock className="w-3 h-3 text-cyan-400" />
                <span className="text-[9px] font-mono uppercase tracking-widest text-cyan-400">Next post</span>
              </div>
              <div className="text-lg font-black text-white tabular-nums">
                {humanUntil(stats.projected_schedule.next_slot_at)}
              </div>
              <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                {stats.projected_schedule.next_slot_at
                  ? formatSlotShort(stats.projected_schedule.next_slot_at)
                  : 'queue empty'}
              </div>
            </div>

            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <FiZap className="w-3 h-3 text-emerald-400" />
                <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-400">Queue depletes</span>
              </div>
              <div className="text-lg font-black text-white tabular-nums">
                {humanUntil(stats.projected_schedule.queue_empty_at)}
              </div>
              <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                {stats.projected_schedule.queue_empty_at
                  ? formatSlotShort(stats.projected_schedule.queue_empty_at)
                  : `< ${stats.total_pending} slots simulated`}
              </div>
            </div>

            <div className="rounded-lg border border-purple-500/30 bg-purple-500/5 p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <FiTarget className="w-3 h-3 text-purple-400" />
                <span className="text-[9px] font-mono uppercase tracking-widest text-purple-400">Pending / cap</span>
              </div>
              <div className="text-lg font-black text-white tabular-nums">
                {stats.total_pending}
              </div>
              <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                ~{Math.ceil(stats.total_pending / stats.projected_schedule.config.max_per_day)} days at max pace
              </div>
            </div>

            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <FiClock className="w-3 h-3 text-amber-400" />
                <span className="text-[9px] font-mono uppercase tracking-widest text-amber-400">Peak hours</span>
              </div>
              <div className="text-xs font-mono text-white leading-tight">
                {(() => {
                  const utcHours = stats.projected_schedule.peak_hours_utc;
                  if (!utcHours.length) return '—';
                  // Convert to local — first + last as a window like "8am-6pm local"
                  const localHours = utcHours.map(h => {
                    const d = new Date();
                    d.setUTCHours(h, 0, 0, 0);
                    return d.getHours();
                  });
                  const isRange = utcHours.length > 3 &&
                    utcHours.every((h, i) => i === 0 || h === utcHours[i - 1] + 1);
                  if (isRange) {
                    return `${utcHours[0]}-${utcHours[utcHours.length - 1]}h UTC (${localHours[0]}-${localHours[localHours.length - 1]}h local)`;
                  }
                  return utcHours.slice(0, 4).map(h => `${h}h`).join(',') + (utcHours.length > 4 ? '…' : '') + ' UTC';
                })()}
              </div>
              <div className="text-[10px] font-mono text-gray-500 mt-0.5">
                {stats.projected_schedule.config.bootstrap
                  ? 'Bootstrap: trader waking hours'
                  : `Top ${stats.projected_schedule.config.peak_top_n} by engagement`}
              </div>
            </div>
          </div>

          {/* Refill warning */}
          {stats.projected_schedule.queue_empty_at && (() => {
            const daysLeft = (new Date(stats.projected_schedule.queue_empty_at).getTime() - Date.now()) / 86400_000;
            if (daysLeft < 2) {
              return (
                <div className="mb-4 rounded-md border border-rose-500/40 bg-rose-500/10 p-3 flex items-start gap-2">
                  <FiAlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <span className="text-rose-300 font-semibold">Queue running low!</span>
                    <span className="text-rose-200 ml-1">Only {Math.max(0, Math.round(daysLeft * 10) / 10)} days of content left. Bulk generate more below or pick a trending topic above.</span>
                  </div>
                </div>
              );
            }
            if (daysLeft < 4) {
              return (
                <div className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 flex items-start gap-2">
                  <FiAlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-200">
                    Queue will last ~{Math.round(daysLeft)} days. Good time to refill soon.
                  </div>
                </div>
              );
            }
            return null;
          })()}

          {/* View toggle between compact timeline and full calendar */}
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">
              {viewMode === 'calendar' ? 'Weekly view — click a post to preview' : `Next ${Math.min(stats.projected_schedule.upcoming_slots.length, 12)} posts`}
            </div>
            <div className="flex rounded-md border border-white/10 p-0.5 bg-white/[0.02]">
              <button
                onClick={() => setViewMode('calendar')}
                className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm flex items-center gap-1 ${viewMode === 'calendar' ? 'bg-cyan-500/20 text-cyan-200' : 'text-slate-400 hover:text-white'}`}
              >
                <FiCalendar className="w-3 h-3" /> Calendar
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm flex items-center gap-1 ${viewMode === 'list' ? 'bg-cyan-500/20 text-cyan-200' : 'text-slate-400 hover:text-white'}`}
              >
                <FiList className="w-3 h-3" /> Timeline
              </button>
            </div>
          </div>

          {viewMode === 'list' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1.5">
              {stats.projected_schedule.upcoming_slots.slice(0, 12).map((slot, i) => (
                <div
                  key={slot}
                  className={`rounded border px-2 py-1.5 text-[10px] font-mono ${
                    i === 0
                      ? 'border-cyan-500/60 bg-cyan-500/10 text-cyan-100'
                      : 'border-white/10 bg-black/30 text-slate-300'
                  }`}
                >
                  <span className="text-gray-500 mr-1">#{i + 1}</span>
                  {formatSlotShort(slot)}
                </div>
              ))}
              {stats.projected_schedule.upcoming_slots.length === 0 && (
                <div className="col-span-4 text-xs text-gray-500 font-mono py-3">
                  Queue empty — add tweets to see the schedule.
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Full calendar view with post previews (paired with pending rows) */}
      {stats?.projected_schedule && viewMode === 'calendar' && pendingRows.length > 0 && (
        <QueueCalendarView
          pendingRows={pendingRows}
          upcomingSlots={stats.projected_schedule.upcoming_slots}
          onRefresh={load}
        />
      )}

      {/* Category stats */}
      {stats && (
        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.03] p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FiZap className="w-4 h-4 text-emerald-400" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-emerald-400">
                Queue balance
              </span>
            </div>
            <div className="flex items-center gap-4 text-xs font-mono">
              <span className="text-emerald-400">{stats.total_pending} pending</span>
              <span className="text-cyan-400">{stats.total_posted} posted</span>
              {stats.total_failed > 0 && <span className="text-rose-400">{stats.total_failed} failed</span>}
              <button
                onClick={postNextNow}
                disabled={posting || stats.total_pending === 0}
                className="px-3 py-1 rounded-md bg-emerald-500/20 border border-emerald-500/40 text-emerald-100 hover:bg-emerald-500/30 disabled:opacity-40 flex items-center gap-1.5"
              >
                {posting ? <FiLoader className="animate-spin w-3 h-3" /> : <FiSend className="w-3 h-3" />}
                Post next now
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
            {(stats.by_category || []).length === 0 ? (
              <div className="col-span-9 text-xs text-gray-500 font-mono">Queue empty. Add tweets below.</div>
            ) : (
              (stats.by_category || []).map(c => {
                const isActive = categoryFilter === c.content_category;
                return (
                  <button
                    key={c.content_category}
                    onClick={() => clickCategory(c.content_category)}
                    className={`px-2 py-2 rounded-md border text-center transition-all hover:scale-105 hover:brightness-125 ${CATEGORY_COLOR[c.content_category] || CATEGORY_COLOR.general} ${isActive ? 'ring-2 ring-white/60 brightness-125' : ''}`}
                    title={`Click to see ${c.pending_count} pending in this category`}
                  >
                    <div className="text-[9px] font-mono uppercase truncate">{c.content_category}</div>
                    <div className="text-lg font-black tabular-nums">{c.pending_count}</div>
                    <div className="text-[9px] font-mono opacity-70">{c.posted_count} sent</div>
                  </button>
                );
              })
            )}
            {categoryFilter && (
              <button
                onClick={() => setCategoryFilter(null)}
                className="px-2 py-2 rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 flex items-center justify-center gap-1"
                title="Clear category filter"
              >
                <FiX className="w-3 h-3" />
                <span className="text-[10px] font-mono">clear</span>
              </button>
            )}
          </div>
          {postResult && (
            <div className={`mt-3 text-xs font-mono p-2 rounded border ${postResult.ok && !postResult.skipped ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-amber-500/10 border-amber-500/30 text-amber-300'}`}>
              {postResult.error && `Error: ${postResult.error}`}
              {postResult.ok && postResult.posted && (
                <>Posted → <a href={postResult.posted.url} target="_blank" rel="noopener noreferrer" className="underline">{postResult.posted.url}</a></>
              )}
              {postResult.ok && postResult.skipped && `Skipped: ${postResult.reason}`}
            </div>
          )}
        </div>
      )}

      {/* Bulk generator */}
      <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.03] p-5">
        <div className="flex items-center gap-2 mb-4">
          <FiZap className="w-4 h-4 text-cyan-400" />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-cyan-400">
            Bulk generate — Fable 5 → queue
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <input
            type="text"
            value={bulkTopic}
            onChange={(e) => setBulkTopic(e.target.value)}
            placeholder="Topic — e.g. Why prop firms want you to fail"
            className="md:col-span-2 px-3 py-2 rounded-md bg-black/40 border border-white/10 text-slate-200 text-sm focus:border-cyan-500/60 focus:outline-none"
          />
          <select
            value={bulkAngle}
            onChange={(e) => setBulkAngle(e.target.value)}
            className="px-3 py-2 rounded-md bg-black/40 border border-white/10 text-slate-200 text-sm focus:border-cyan-500/60 focus:outline-none"
          >
            {ANGLES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
          <select
            value={bulkCategory}
            onChange={(e) => setBulkCategory(e.target.value)}
            className="px-3 py-2 rounded-md bg-black/40 border border-white/10 text-slate-200 text-sm focus:border-cyan-500/60 focus:outline-none"
          >
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={bulkCount}
            onChange={(e) => setBulkCount(Number(e.target.value))}
            className="px-3 py-2 rounded-md bg-black/40 border border-white/10 text-slate-200 text-sm focus:border-cyan-500/60 focus:outline-none"
          >
            {[5, 8, 10, 12, 15, 20, 25].map(n => <option key={n} value={n}>{n} tweets</option>)}
          </select>
          <button
            onClick={bulkGenerate}
            disabled={!bulkTopic.trim() || bulkGenerating}
            className="px-4 py-2 rounded-md bg-cyan-500/20 border border-cyan-500/40 text-cyan-100 text-sm font-mono hover:bg-cyan-500/30 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {bulkGenerating ? <FiLoader className="animate-spin" /> : <FiSend />}
            {bulkGenerating ? 'Generating…' : 'Generate'}
          </button>
        </div>
        {bulkError && (
          <div className="mt-3 text-xs font-mono text-rose-400 flex items-center gap-1">
            <FiAlertCircle /> {bulkError}
          </div>
        )}
        {lastBatchInfo && (
          <div className="mt-3 text-xs font-mono text-emerald-400 flex items-center gap-1">
            <FiCheckCircle /> {lastBatchInfo.count} tweets added — cost ${lastBatchInfo.cost.toFixed(4)}
          </div>
        )}
      </div>

      {/* Manual add */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center gap-2 mb-3">
          <FiPlus className="w-4 h-4 text-slate-400" />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
            Add manual tweet
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <textarea
            value={manualText}
            onChange={(e) => setManualText(e.target.value)}
            placeholder="Write your tweet…"
            rows={2}
            className="w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-slate-200 text-sm focus:border-cyan-500/60 focus:outline-none resize-none"
          />
          <div className="flex items-center gap-3">
            <select
              value={manualCategory}
              onChange={(e) => setManualCategory(e.target.value)}
              className="px-3 py-1.5 rounded-md bg-black/40 border border-white/10 text-slate-200 text-sm focus:border-cyan-500/60 focus:outline-none"
            >
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className={`text-[10px] font-mono ${manualText.length > 280 ? 'text-rose-400' : manualText.length > 260 ? 'text-amber-400' : 'text-gray-500'}`}>
              {manualText.length}/280
            </span>
            <button
              onClick={addManual}
              disabled={!manualText.trim() || adding || manualText.length > 280}
              className="ml-auto px-3 py-1.5 rounded-md bg-emerald-500/20 border border-emerald-500/40 text-emerald-100 text-sm font-mono hover:bg-emerald-500/30 disabled:opacity-40 flex items-center gap-1.5"
            >
              {adding ? <FiLoader className="animate-spin w-3 h-3" /> : <FiPlus className="w-3 h-3" />}
              Add to queue
            </button>
          </div>
        </div>
      </div>

      {/* Queue list */}
      <div id="queue-rows-section" className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
              Queue rows
            </span>
            {categoryFilter && (
              <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border flex items-center gap-1 ${CATEGORY_COLOR[categoryFilter] || CATEGORY_COLOR.general}`}>
                {categoryFilter}
                <button onClick={() => setCategoryFilter(null)} className="hover:text-white" title="Clear">
                  <FiX className="w-3 h-3" />
                </button>
              </span>
            )}
            <span className="text-[10px] font-mono text-gray-500">
              {rows.length} {rows.length === 1 ? 'row' : 'rows'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-white/10 p-0.5 bg-white/[0.02]">
              {(['pending', 'posted', 'failed', 'all'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm ${filter === f ? 'bg-cyan-500/20 text-cyan-200' : 'text-slate-400 hover:text-white'}`}
                >
                  {f}
                </button>
              ))}
            </div>
            <button onClick={load} className="p-1 text-slate-500 hover:text-white" title="Refresh">
              <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="text-xs text-gray-500 font-mono py-8 text-center">
            {loading ? 'Loading…' : 'Empty. Add or generate tweets above.'}
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map(r => (
              <QueueRowCard
                key={r.id}
                row={r}
                onDelete={() => deleteRow(r.id)}
                onEditText={(newText) => editText(r, newText)}
                onPriorityChange={(p) => setPriority(r.id, p)}
                onRefresh={load}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const QueueRowCard: React.FC<{
  row: QueueRow;
  onDelete: () => void;
  onEditText: (text: string) => void;
  onPriorityChange: (p: number) => void;
  onRefresh?: () => void;
}> = ({ row, onDelete, onEditText, onPriorityChange, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(row.text);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const isPending = row.status === 'pending' || row.status === 'approved';

  const save = () => {
    onEditText(text);
    setEditing(false);
  };

  const generateImage = async () => {
    if (row.media_url) {
      if (!confirm('This tweet already has an image. Delete it and regenerate?')) return;
      await supabase.from('tweet_queue').update({ media_url: null }).eq('id', row.id);
    }
    setGeneratingImage(true);
    setImageError(null);
    try {
      const { data: session } = await supabase.auth.getSession();
      const token = session?.session?.access_token;
      const res = await fetch('/api/growth-api?action=generate-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ queue_id: row.id }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
      onRefresh?.();
    } catch (e: any) {
      setImageError(e?.message || String(e));
    } finally {
      setGeneratingImage(false);
    }
  };

  const deleteImage = async () => {
    if (!confirm('Delete image? Tweet will post without media.')) return;
    await supabase.from('tweet_queue').update({ media_url: null }).eq('id', row.id);
    onRefresh?.();
  };

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 hover:border-cyan-500/30 transition-colors">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${STATUS_COLOR[row.status] || STATUS_COLOR.pending}`}>
            {row.status}
          </span>
          <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border ${CATEGORY_COLOR[row.content_category] || CATEGORY_COLOR.general}`}>
            {row.content_category}
          </span>
          <span className="text-[10px] font-mono text-gray-500">
            {row.source} · {new Date(row.created_at).toLocaleDateString()}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {row.posted_tweet_id && (
            <a
              href={`https://x.com/i/status/${row.posted_tweet_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cyan-400 hover:text-cyan-300 text-[10px] font-mono flex items-center gap-1"
            >
              <FiEye className="w-3 h-3" /> View
            </a>
          )}
          {isPending && !row.media_url && (
            <button
              onClick={generateImage}
              disabled={generatingImage}
              className="px-1.5 py-0.5 rounded text-[10px] font-mono text-fuchsia-300 hover:bg-fuchsia-500/10 border border-fuchsia-500/30 disabled:opacity-40 flex items-center gap-1"
              title="Generate viral image with Nano Banana (~$0.04, 8-12s)"
            >
              {generatingImage
                ? <FiLoader className="w-3 h-3 animate-spin" />
                : <FiImage className="w-3 h-3" />}
              {generatingImage ? 'Generating…' : 'Image'}
            </button>
          )}
          {isPending && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="p-1 text-slate-500 hover:text-white"
              title="Edit"
            >
              <FiEdit3 className="w-3 h-3" />
            </button>
          )}
          {isPending && (
            <button
              onClick={onDelete}
              className="p-1 text-slate-500 hover:text-rose-400"
              title="Delete"
            >
              <FiTrash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
      {editing ? (
        <div className="space-y-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            className="w-full px-2 py-1.5 rounded-md bg-black/40 border border-white/10 text-sm text-slate-200 focus:border-cyan-500/60 focus:outline-none resize-none"
          />
          <div className="flex items-center justify-end gap-2">
            <span className={`text-[10px] font-mono mr-auto ${text.length > 280 ? 'text-rose-400' : text.length > 260 ? 'text-amber-400' : 'text-gray-500'}`}>
              {text.length}/280
            </span>
            <button
              onClick={() => { setText(row.text); setEditing(false); }}
              className="px-2 py-1 text-[10px] font-mono text-slate-400 hover:text-white"
            >
              <FiX className="w-3 h-3 inline" /> Cancel
            </button>
            <button
              onClick={save}
              disabled={text.length > 280 || text.length === 0}
              className="px-2 py-1 rounded-md bg-emerald-500/20 border border-emerald-500/40 text-emerald-100 text-[10px] font-mono hover:bg-emerald-500/30 disabled:opacity-40 flex items-center gap-1"
            >
              <FiCheck className="w-3 h-3" /> Save
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="text-sm text-slate-200 whitespace-pre-wrap">{row.text}</div>
          {row.media_url && (
            <div className="mt-2 relative group">
              <img
                src={row.media_url}
                alt="Tweet media"
                className="rounded-lg border border-white/10 max-h-64 w-auto object-cover"
                loading="lazy"
              />
              {isPending && (
                <button
                  onClick={deleteImage}
                  className="absolute top-1 right-1 p-1.5 rounded bg-black/60 border border-rose-500/40 text-rose-300 opacity-0 group-hover:opacity-100 hover:bg-rose-500/20 transition-opacity"
                  title="Remove image (tweet will post without media)"
                >
                  <FiTrash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
          {imageError && (
            <div className="mt-2 text-[10px] font-mono text-rose-400 flex items-start gap-1">
              <FiAlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" /> Image gen failed: {imageError}
            </div>
          )}
          {row.error_message && (
            <div className="mt-2 text-[10px] font-mono text-rose-400 flex items-start gap-1">
              <FiAlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {row.error_message}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ContentQueueManager;
